import { BreadcrumbItem, Breadcrumbs } from "@vds/breadcrumbs";
import { ButtonGroup } from "@vds/buttons";
import { Icon } from "@vds/icons";
import { Loader } from "@vds/loaders";
import { Modal, ModalBody } from "@vds/modals";
import { RadioButtonGroup } from "@vds/radio-buttons";
import { DropdownSelect } from "@vds/selects";
import { Toggle } from "@vds/toggles";
import { Body, Title } from "@vds/typography";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useLocation, createSearchParams } from "react-router-dom";
import { fetchRoiWithCvApps } from "../../api/RoiServiceManager";
import CustomHOCTable from "../../global/components/Tables/CustomHOCTable";
import { CVAPP_CAMERAADJUSTMENT, CVAPP_OBJECTBLUR } from "../../global/constants/cvappConstants";
import { PAGE_IDENTIFIERS } from "../../global/constants/tableConstants";
import { QUERY_PARAMS, ROUTES } from "../../global/constants/routesConstants";
import { usePaginationReset, setDetailNavSource } from "../../hooks/usePaginationReset";
import {
  BASE_LICENSE_SKUS,
  DEVICE_FIELDS,
  isV2Sku,
  LICENSE_ACTION_TYPES,
  LICENSE_ACTIONS,
  LICENSE_ERROR_DESCRIPTIONS,
  LICENSE_LABELS,
  LICENSE_MODAL_TEXTS,
  LICENSE_NOTIFICATION_TYPES,
  LICENSE_PERMISSION,
  LICENSE_STATUS,
  LICENSE_SKU_PATTERNS,
  LICENSE_STATUS_LABELS,
  LICENSE_TYPES,
  LICENSE_UI_TEXT,
  LICENSE_VALUES,
  LICENSE_WARNINGS,
  licensesMessage,
  VISION_SKUS_DISPLAYNAME,
  LICENSE_ORIGIN,
  UNSUBSCRIBE_FLOW_TEXTS
} from "../../global/constants/licensesConstants";
import { getCameraNameFromFovId, getCompanionObjectById, getFilterOptions, getFovObjWithStreamId } from "../../global/utility/deviceTableUtils";
import { hasPermission } from "../../global/utility/permissionUtils";
import { getLicenseOveruseState, getLicenseTypeDisplayed, handleApiError } from "../../global/utility/Utils";
import NotificationBanner from "../../layouts/NotificationBanner";
import ChangeLicenseModal from "../../global/components/Modals/ChangeLicenseModal";
import { changeLicenseType, showNotificationBanner, updateTableFilterState } from "../../store/actions";
import { fetchLicenses } from "../../store/actions/license-actions";
import { fetchFovByLicense, resumeLicense, suspendLicense } from "./LicensesServices";
import { HTTP_STATUS_CODES } from "../../global/constants/constants";
import ErrorTemplate from "../../global/components/errorTemplate/ErrorTemplate";
import { Box, Tooltip } from "@mui/material";
import "./LicenseDetails.scss";


function LicenseDetails() {
  const { activeSession } = useSelector((state) => state.sessionReducer.activeSession);
  const { companionDeviceList, cameraList, fovList, roiList = [] } = useSelector((state) => state?.deviceReducer) || {};
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location?.state;
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const { bannerInfo } = useSelector((state) => state?.notificationBanner);
  const [fovData, setFovData] = useState([]);
  const { changeLicenseTypeSuccess, changeLicenseTypeFailure, fovByLicenseCache, selectedLicenseRecord, licenses } = useSelector((state) => state.licenseReducer);
  const record = selectedLicenseRecord;
  const billingAccount = record?.billingAccount || "";
  const [loading, setLoading] = useState(false);
  const [fetchFailed, setFetchFailed] = useState(false);
  const siteReducer = useSelector((state) => state.sitesReducer.siteTagList);
  const usersAssignedSiteids = siteReducer?.sites?.map(s => s.id) || [];
  const licenseList = licenses;
  const licensetype = record?.license;
  const displayLicense = getLicenseTypeDisplayed(licensetype, licenses);
  const isEALicenseType = licensetype === LICENSE_SKU_PATTERNS.ENROLLED_APPEARANCE || licensetype === LICENSE_SKU_PATTERNS.ENROLLED_APPEARANCE_V2;
  const [licenseStatusLoading, setLicenseStatusLoading] = useState({});
  const canEditLicense = hasPermission(LICENSE_PERMISSION.TYPE, LICENSE_ACTIONS.EDIT);
  const showErrorTemplate = fetchFailed && fovData?.length === 0;
  const isGracePeriodExpired = useMemo(() => {
    if (!licenseList || !Array.isArray(licenseList)) return false;
    return licenseList?.some((lic) => getLicenseOveruseState(lic.overusestartedon) === LICENSE_STATUS.EXPIRED);
  }, [licenseList]);
  const [isLicenseChanging, setIsLicenseChanging] = useState(false);
  const isFromCameraDetails = state?.originPage === "cameraDetails";
  const cameraData = state?.cameraData;
  const showModal = (deviceId) => {
    setSelectedDeviceId(deviceId);
    setIsModalVisible(true);
  };

  const handleLicenseConfirm = (selectedSku) => {
    setIsModalVisible(false);
    setIsLicenseChanging(true);
    dispatch(changeLicenseType(billingAccount, selectedDeviceId, selectedSku, licensetype, navigate, fovList));
  };

  useEffect(() => {
    if (!changeLicenseTypeSuccess && !changeLicenseTypeFailure) return;
    if (!licensetype) return;
    if (changeLicenseTypeSuccess) {
      (async () => {
        try {
          const response = await fetchFovByLicense(billingAccount, licensetype);
          const allFovs = response?.actionresult?.ViewFoVByLicense || [];
          const cacheKey = `${billingAccount}:${licensetype}`;
          const filteredFovs = usersAssignedSiteids.length > 0 ? allFovs.filter(f => usersAssignedSiteids.includes(f?.siteid)) : allFovs;
          dispatch({ type: LICENSE_ACTION_TYPES.SET_FOV_BY_LICENSE_CACHE, cacheKey, data: filteredFovs });
          setFovData(filteredFovs);
        } catch (error) {
          console.error("Failed to refetch FOV data after license change:", error);
        } finally {
          setIsLicenseChanging(false);;
          dispatch({ type: "RESET_CHANGE_LICENSE_TYPE" });
        }
      })();
    } else {
      setIsLicenseChanging(false);
      dispatch({ type: "RESET_CHANGE_LICENSE_TYPE" });
    }
  }, [changeLicenseTypeSuccess, changeLicenseTypeFailure]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNavigateToCompanionDevicePage = (companionId, recordName) => {
    if (!companionId) {
      console.log(LICENSE_WARNINGS.COMPANION_ID_NOT_PROVIDED);
      return;
    }

    const companionDeviceData = getCompanionObjectById(companionId, companionDeviceList);

    if (companionDeviceData) {
      const state = {
        data: { ...companionDeviceData },
        title: `More info for ${recordName}`,
        fields: companionDeviceData.fields || {}
      };

      setDetailNavSource(PAGE_IDENTIFIERS.LICENSES);
      navigate(`${ROUTES.COMPANION_DEVICE_INFO_PAGE}?${QUERY_PARAMS.CCD_ID}=${encodeURIComponent(companionDeviceData.id)}`, { state });
    } else {
      console.log(LICENSE_WARNINGS.COMPANION_DEVICE_NOT_FOUND);
    }
  };


  const roiByDevice = useMemo(() => {
    const map = {};
    roiList?.forEach((roi) => {
      if (roi?.cvapps && roi.parentdeviceid) {
        if (!map[roi.parentdeviceid]) map[roi.parentdeviceid] = [];
        map[roi.parentdeviceid].push(roi);
      }
    });
    return map;
  }, [roiList]);

  const dataList = fovData?.map((fov, index) => {
    const matchingROI = roiByDevice[fov?.streamid] || [];
    const uniqueImages = [...new Set(matchingROI.map((roi) => roi.cvapps[0].image))];
    const apps = uniqueImages.length;

    // Below to determine the base license associated for Enrolled appearance license
    const fovObject = fovList?.find((f) => f.id === fov?.streamid);
    const parentLicenseSku = fovObject?.licenses?.find((sku) => BASE_LICENSE_SKUS.includes(sku)) || null;
    const parentLicenseName = parentLicenseSku ? (VISION_SKUS_DISPLAYNAME[parentLicenseSku] || parentLicenseSku) : LICENSE_UI_TEXT.NA;

    return {
      key: index.toString(),
      cameraName: fov?.cameraname || LICENSE_UI_TEXT.NA,
      cameraId: fov?.cameraid || null,
      streamName: fov?.streamname || LICENSE_UI_TEXT.NA,
      Site: fov?.sitename || LICENSE_UI_TEXT.NA,
      companion: fov?.companionname || LICENSE_UI_TEXT.NA,
      companionid: fov?.companionid || null,
      apps: apps,//fov?.apps || 0,
      actions: "TBD",
      deviceId: fov?.streamid,
      licenseStatus: fov?.licensestatus || LICENSE_STATUS.ACTIVE,
      sku: fov?.sku,
      parentLicenseSku,
      parentLicenseName
    };
  }) || [];


  const handleNavigateToCameraDetails = (cameraId, cameraName) => {
    if (!cameraId) {
      console.log(LICENSE_WARNINGS.CAMERA_ID_NOT_PROVIDED);
      return;
    }

    const cameraData = cameraList?.find((camera) => camera.id === cameraId);

    if (cameraData) {
      setDetailNavSource(PAGE_IDENTIFIERS.LICENSES);
      navigate(`/cameraInfoPage?cameraId=${cameraData?.id}`, {
        state: {
          data: cameraData
        }
      });
    } else {
      console.log(LICENSE_WARNINGS.CAMERA_DATA_NOT_FOUND);
    }
  };

  const handleEditAppsClick = (streamId, unsubscribeFlow = true) => {
    if (!streamId) {
      console.log(LICENSE_WARNINGS.STREAM_ID_NOT_PROVIDED);
      return;
    }

    const fovObject = fovList?.find((f) => f.id === streamId);
    if (!fovObject) {
      console.log(LICENSE_WARNINGS.FOV_OBJECT_NOT_FOUND);
      return;
    }

    const cameraData = cameraList?.find((camera) => camera.id === fovObject.parentdeviceid);
    if (!cameraData) {
      console.log(LICENSE_WARNINGS.CAMERA_DATA_NOT_FOUND);
      return;
    }

    setDetailNavSource(PAGE_IDENTIFIERS.LICENSES);
    const fov = fovData?.find((f) => f?.streamid === streamId);
    const licenseStatus = fov?.licensestatus || LICENSE_STATUS.ACTIVE;
    navigate("/roi", {
      state: {
        data: cameraData,
        streamData: getFovObjWithStreamId(streamId, fovList),
        FOVId: streamId,
        FOVSnapshot: fovObject?.customdata?.imageid,
        fieldOfViews: fovObject?.name,
        FOVName: fovObject?.name,
        originPage: "licenseDetails",
        licenseStatus,
        isUnsubscribeFlow: unsubscribeFlow,
      },
    });
  };
  const handleToggleLicenseStatus = async (streamId, sku, isCurrentlyActive) => {
    if (!streamId || !sku || !billingAccount || licenseStatusLoading[streamId]) {
      return;
    }
    const isLicenseAvailable = record?.availablelicenses > 0 && record?.sku === sku;
    setLicenseStatusLoading((prev) => ({ ...prev, [streamId]: true }));
    try {
      let response;
      // Handle suspend operation
      if (isCurrentlyActive) {
        response = await suspendLicense(billingAccount, streamId, sku);
      }
      // Handle resume operation - check license availability
      else if (isLicenseAvailable) {
        response = await resumeLicense(billingAccount, streamId, sku);
      }
      // No license available - show notification and return
      else {
        dispatch(
          showNotificationBanner({
            type: LICENSE_NOTIFICATION_TYPES.NO_LICENSE_AVAILABLE,
            priority: "critical",
            showNotification: true
          })
        );
        return;
      }

      // Update FOV data and refresh license store if API call was successful
      if (response?.status === HTTP_STATUS_CODES.OK) {
        dispatch(fetchLicenses({ accountId: billingAccount, origin: LICENSE_ORIGIN.LICENSE }));
        const nextStatus = isCurrentlyActive ? LICENSE_STATUS.SUSPENDED : LICENSE_STATUS.ACTIVE;
        setFovData((prev) => {
          if (!Array.isArray(prev)) {
            return prev;
          }
          return prev.map((item) =>
            item.streamid === streamId ? { ...item, licensestatus: nextStatus } : item
          );
        });
      }
    } catch (error) {
      dispatch(
        showNotificationBanner({
          type: isCurrentlyActive
            ? LICENSE_NOTIFICATION_TYPES.SUSPEND_FAILED
            : LICENSE_NOTIFICATION_TYPES.RESUME_FAILED,
          priority: "critical",
          showNotification: true
        })
      );
      console.error("Failed to toggle license status", error);
    } finally {
      setLicenseStatusLoading((prev) => {
        if (!prev) return {};
        const { [streamId]: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleParentLicenseClick = (e, licenseRecord) => {
    e.preventDefault();
    e.stopPropagation();
    if (!licenseRecord.parentLicenseSku) return;
    dispatch({
      type: LICENSE_ACTION_TYPES.SET_SELECTED_LICENSE_RECORD,
      payload: {
        ...record,
        sku: licenseRecord.parentLicenseSku,
        license: licenseRecord.parentLicenseSku,
        billingAccount
      }
    });
    setDetailNavSource(PAGE_IDENTIFIERS.LICENSES);
    navigate("/licensedetails", { state: { pendingSearchTerm: licenseRecord.cameraName || "" }, replace: true });
  };

  const cameraColumn = {
      title: () => (
        <span className="license-details-table-header-title">
          <Title size="small" bold>
            {LICENSE_UI_TEXT.CAMERA}
          </Title>
        </span>
      ),
      dataIndex: "cameraName",
      key: "cameraName",
      width: "17%",
      ellipsis: true,
      render: (text, record) => {
        if (isGracePeriodExpired || !record.cameraId) {
          return <span className="license-details-camera-link-auto">{text}</span>;
        }
        return (
          <span
            className="license-details-table-link"
            onClick={() => handleNavigateToCameraDetails(record.cameraId, text)}
          >
            {text}
          </span>
        );
      }
  };

  const streamColumn = {
      title: () => (
        <span className="license-details-table-header-title">
          <Title size="small" bold>
            {LICENSE_UI_TEXT.STREAM}
          </Title>
        </span>
      ),
      dataIndex: "streamName",
      key: "streamName",
      width: "17%",
      ellipsis: true,
  };

  const siteColumn = {
      title: () => (
        <span className="license-details-table-header-title">
          <Title size="small" bold>
            {LICENSE_UI_TEXT.SITE}
          </Title>
        </span>
      ),
      dataIndex: "Site",
      key: "Site",
      width: "17%",
      isFilter: true,
      ellipsis: true,
    filter: getFilterOptions(dataList, "Site", "licenseDetailPage")
  };

  const companionColumn = {
      title: () => (
        <span className="license-details-table-header-title">
          <Title size="small" bold>
            {LICENSE_UI_TEXT.COMPANION}
          </Title>
        </span>
      ),
      dataIndex: "companion",
      key: "companion",
      width: "17%",
      isFilter: true,
      filter: getFilterOptions(dataList, "companion"),
      ellipsis: true,
      render: (text, record) => {
        if (isGracePeriodExpired) {
          return <span className="license-details-camera-link-auto">{text}</span>;
        }
        return (
          <span
            className="license-details-table-link"
            data-testid="licenses_companionNameTitleTextlink"
            onClick={() => handleNavigateToCompanionDevicePage(record.companionid)}
          >
            {text}
          </span>
        );
    }
  };

  const appsColumn = {
      title: () => (
        <span className="license-details-table-header-title">
          <Title size="small" bold>
            {LICENSE_UI_TEXT.APPS}
          </Title>
        </span>
      ),
      dataIndex: "apps",
      key: "apps",
      width: "10%",
      render: (text, record) => {
        const hasApps = text !== 0;
        return (
          <div
          className={hasApps ? "license-details-table-link" : "license-details-apps-link-disabled"}
            data-testid="licenses_appsLink"
            onClick={() => { hasApps && handleEditAppsClick(record.deviceId, false) }}
          >
            {`${text} ${LICENSE_UI_TEXT.APPS_SUFFIX}`}
          </div>
        );
    }
  };

  const changeLicenseColumn = {
      title: () => (
        <span className="license-details-table-header-title">
          <Title size="small" bold>
            {LICENSE_UI_TEXT.CHANGE_LICENSE}
          </Title>
        </span>
      ),
      dataIndex: "deviceId",
      key: "actions",
      width: "13%",
      align: "center",
      render: (deviceId, record) => {
        return (
          <Tooltip title="Change license type">
            <span
              data-testid="licenses_actionsBillUpOrDownIcon"
              onClick={() => {
                if (!canEditLicense) {
                  showPermissionsBanner();
                } else {
                  showModal(deviceId);
                }
              }}
              style={{cursor:"pointer"}}
            >
              <Icon
                name="switch"
                size="medium"
                color="#000000"
                ariaLabel="Change license type"
                ariaHidden="true"
              />
            </span>
          </Tooltip>
        );
    }
  };

  const licenseStatusColumn = {
      title: () => (
        <span className="license-details-table-header-title">
          <Title size="small" bold>
            {LICENSE_UI_TEXT.LICENSE_STATUS}
          </Title>
        </span>
      ),
      dataIndex: "licenseStatus",
      key: "licenseStatus",
      width: "13%",
      render: (status, record) => {
        const isActive = (status || "").toLowerCase() === LICENSE_STATUS.ACTIVE;
        const isUpdatingStatus = Boolean(licenseStatusLoading[record.deviceId]);
        return (
          <div className="license-details-license-status-container">
            <Toggle
              on={isActive}
              surface="light"
              showText={true}
              textWeight="regular"
              textSize="small"
              textPosition="right"
              statusText={(value) => (value ? LICENSE_STATUS_LABELS.ACTIVE : LICENSE_STATUS_LABELS.SUSPENDED)}
              disabled={isUpdatingStatus || isGracePeriodExpired || !canEditLicense}
              onChange={() => {
                if (!canEditLicense) {
                  showPermissionsBanner();
                  return;
                }
              handleToggleLicenseStatus(record.deviceId, record.sku, isActive);
              }}
            />
            {isUpdatingStatus && (
              <Loader
                active={true}
                fullscreen={false}
              />
            )}
          </div>
        );
    },
  };

  const parentLicenseColumn = {
    title: () => (
      <Title size="small" bold>
        Base License
      </Title>
    ),
    dataIndex: "parentLicenseName",
    key: "parentLicenseName",
    width: "13%",
    render: (text, record) => (
      <div
        className={record.parentLicenseSku ? "license-details-table-link" : "license-details-apps-link-disabled"}
        data-testid="licenses_parentLicenseLink"
        onClick={(e) => record.parentLicenseSku && handleParentLicenseClick(e, record)}
      >
        {text}
      </div>
    )
  };

  const columns = [
    cameraColumn,
    streamColumn,
    siteColumn,
    companionColumn,
    appsColumn,
    ...(isEALicenseType ? [parentLicenseColumn] : [changeLicenseColumn, licenseStatusColumn])
  ];

  const showPermissionsBanner = () => {
    const notificationInfo = {
      type: LICENSE_NOTIFICATION_TYPES.NO_PERMISSION,
      priority: "critical",
      showNotification: true
    };
    dispatch(showNotificationBanner(notificationInfo));
  };

  useEffect(() => {
    if (!record) {
      navigate("/licenses");
      return;
    }
  }, [record]);

  const handleFetchFovError=(data)=>{
    if (data?.length > 0) handleApiError(LICENSE_WARNINGS.WARNING, LICENSE_ERROR_DESCRIPTIONS.FETCH_FOV_ERROR);
  }

  const fetchFovData = async () => {
    if (!billingAccount || !licensetype) return;
    const cacheKey = `${billingAccount}:${licensetype}`;

    const cached = fovByLicenseCache?.[cacheKey];
    if (cached) {
      // Show cached data immediately while background refresh runs
      setFovData(cached);
    }

    // Show loader only when there is nothing cached to display yet
    if (!cached) setLoading(true);

    try {
      const response = await fetchFovByLicense(billingAccount, licensetype);
      const allFovs = response?.actionresult?.ViewFoVByLicense || [];
      const filteredFovs = usersAssignedSiteids.length > 0 ? allFovs.filter(f => usersAssignedSiteids.includes(f?.siteid)) : allFovs;
      dispatch({ type: LICENSE_ACTION_TYPES.SET_FOV_BY_LICENSE_CACHE, cacheKey, data: filteredFovs });
      setFovData(filteredFovs);
      setFetchFailed(false);
    } catch (error) {
      handleFetchFovError(cached);
      console.error("Error fetching FOV data:", error);
      if (!cached) setFetchFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh=()=>{
    setFetchFailed(false);
    fetchFovData();
  }

  // Apply pending search term passed via navigation state — dispatched here instead of before navigate
  // because CustomHOCTable's cleanup clears searchTerm on unmount, overwriting any pre-navigate dispatch
  useEffect(() => {
    const pendingSearchTerm = location.state?.pendingSearchTerm;
    if (pendingSearchTerm !== undefined) {
      dispatch(updateTableFilterState({ pageIdentifier: PAGE_IDENTIFIERS.LICENSES, propName: "searchTerm", value: pendingSearchTerm }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!billingAccount || !licensetype) return;
    fetchFovData();
}, [billingAccount, licensetype]);


  const LicenseDetailsTable = CustomHOCTable();
  return (
    <div className="show-loader-componet license-details-page-container">
      {isLicenseChanging && (
        <div className="loader-absolute-container">
          <Loader
            active={true}
            fullscreen={false}
            showOverlay={true}
            surface="light"
          />
        </div>
      )}
      <ChangeLicenseModal
        isOpen={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        onConfirm={handleLicenseConfirm}
        currentSku={licensetype}
        licenseList={licenseList}
        roiList={roiList}
        deviceId={selectedDeviceId}
        onUnsubscribeClick={() => {
          setIsModalVisible(false);
          handleEditAppsClick(selectedDeviceId,true);
        }}
      />
      <Breadcrumbs surface="light">
        {/* First Breadcrumb: Cameras or Settings */}
        <BreadcrumbItem>
          {isGracePeriodExpired ? (
            <span className="license-details-disabled-text">
              {isFromCameraDetails
                ? UNSUBSCRIBE_FLOW_TEXTS.CAMERAS_BREADCRUMB
                : LICENSE_UI_TEXT.SETTINGS}
            </span>
          ) : (
            <Link
              data-testid={
                isFromCameraDetails
                  ? "polygon_devicesLink"
                  : "licenseDetails_settingsLink"
              }
              to={isFromCameraDetails ? "/devices" : "/settings"}
              state={isFromCameraDetails ? { tabName: "Camera" } : undefined}
              className="breadcrums-item_link"
            >
              {isFromCameraDetails
                ? UNSUBSCRIBE_FLOW_TEXTS.CAMERAS_BREADCRUMB || "Cameras"
                : LICENSE_UI_TEXT.SETTINGS}
            </Link>
          )}
        </BreadcrumbItem>

        {/* Second Breadcrumb: Camera Name or Licenses */}
        <BreadcrumbItem>
          {isGracePeriodExpired ? (
            <span className="license-details-disabled-text">
              {isFromCameraDetails
                ? cameraData?.name
                : LICENSE_UI_TEXT.LICENSES}
            </span>
          ) : (
            <Link
              data-testid={
                isFromCameraDetails
                  ? "polygon_cameraInfoPageLink"
                  : "licenseDetails_licensesLink"
              }
              to={
                isFromCameraDetails
                  ? {
                    pathname: "/cameraInfoPage",
                    search: `?${createSearchParams({ cameraId: cameraData?.id })}`,
                  }
                  : "/licenses"
              }
              state={isFromCameraDetails ? { data: cameraData } : undefined}
              className="breadcrums-item_link"
            >
              {isFromCameraDetails
                ? cameraData?.name
                : LICENSE_UI_TEXT.LICENSES}
            </Link>
          )}
        </BreadcrumbItem>

        {/* Third Breadcrumb: License details display */}
        <BreadcrumbItem>{displayLicense}</BreadcrumbItem>
      </Breadcrumbs>
    <div className="license-details-page-title-wrapper">
      <Title size="large">
        {displayLicense === LICENSE_TYPES.NO_LICENSE
          ? displayLicense
          : `${displayLicense} ${LICENSE_UI_TEXT.LICENSES}`}
      </Title>
    </div>
    <div className="license-details-table-container">
      {!loading && (showErrorTemplate || fovData.length === 0) && (
        <Box sx={{ height: "600px" }}>
          {showErrorTemplate ? (
            <ErrorTemplate
              title={LICENSE_ERROR_DESCRIPTIONS.FETCH_STREAMS_FAILED}
              description={LICENSE_ERROR_DESCRIPTIONS.FETCH_STREAMS_FAILED_INFO}
              showRefreshButton={true}
              onRefresh={handleRefresh}
              templateType="error"
            />
          ) : (
            <ErrorTemplate
              title={LICENSE_WARNINGS.NO_DATA}
              description={LICENSE_WARNINGS.NO_DATA_INFO}
              showRefreshButton={true}
              templateType="info"
              onRefresh={handleRefresh}
            />
          )}
        </Box>
      )}
      {(loading || (!showErrorTemplate && fovData.length > 0)) && (
        <LicenseDetailsTable
          showSearch={true}
          showTitle={false}
          columns={columns}
          dataList={dataList}
          rowKey="key"
          addDetailsButton={false}
          noFilter={false}
          loading={loading}
          pageIdentifier={PAGE_IDENTIFIERS.LICENSES}
          useReduxPersistence={true}
        />
      )}
    </div>
  </div>
);
}

export default LicenseDetails;
